import { useNavigate } from "react-router-dom";
import { useQuery } from "../../api/useQuery.js";
import { getUpcomingEvents, getOpenCheckItems, getSubjectList, getAllAITests } from "../../api/queries.js";
import { DueBadge } from "../../components/calendar/DueBadge.js";
import { DashboardPanel } from "../../components/molecules/DashboardPanel.js";
import { SubjectPanel } from "../../components/subjects/SubjectPanel.js";
import { TaskList } from "../../components/tasks/TaskList.js";
import { LoadingRows, QueryStatus } from "../../components/ui/LoadingPlaceholders.js";
import { PageTitle } from "../../components/ui/PageTitle.js";
import { ViewModeToggle, type ViewMode } from "../../components/ui/ViewModeToggle.js";
import { usePersistedViewMode } from "../../components/ui/usePersistedViewMode.js";
import { EVENT_TYPE_COLORS, UPCOMING_MAX, homeUpcomingArgs, upcomingCutoffKey } from "../../constants.js";
import { TestList } from "../../components/organisms/TestList.js";
import { ColorDot, EmptyState, ErrorState, Grid, useT } from "@mmm/ui";
export function HomeScreen() {
  const navigate = useNavigate();
  const t = useT();
  const [taskView, setTaskView] = usePersistedViewMode("home-tasks", "list" satisfies ViewMode);

  // All future events (today onward), ordered by date — see homeUpcomingArgs.
  const events = useQuery(getUpcomingEvents, homeUpcomingArgs());
  const tasks = useQuery(getOpenCheckItems, undefined);
  const subjects = useQuery(getSubjectList, undefined);
  const tests = useQuery(getAllAITests, undefined);

  // Upcoming card: all events within the next 14 days, then backfill with the
  // nearest events beyond that until the list reaches UPCOMING_MAX.
  const allFuture = events.data || [];
  const cutoff = upcomingCutoffKey();
  const within = allFuture.filter((e) => e.startDate <= cutoff);
  const beyond = allFuture.filter((e) => e.startDate > cutoff);
  const upcoming = [...within, ...beyond.slice(0, Math.max(0, UPCOMING_MAX - within.length))];

  return (
    <div className="mmm-dashboard-screen">
      <PageTitle title={t("nav.home")} />
      <QueryStatus queries={[events, tasks, subjects, tests]} />

      <div className="mmm-home-dashboard">
        <SubjectPanel
          title={t("nav.subjects")}
          subtitle={t("home.subjectsSubtitle")}
          subjects={subjects.data || []}
          loading={subjects.loading}
          hasError={Boolean(subjects.error) && !subjects.waiting}
          onRetry={subjects.retry}
          emptyTitle={t("subjects.emptyTitle")}
          emptyMessage={t("subjects.emptyMessage")}
          addNavigateOnCreate={false}
          viewStorageKey="home-subjects"
        />

        <Grid columns={3} minItemWidth={220} gap="lg" className="mmm-home-lower-grid">
          <DashboardPanel title={t("nav.tests")}>
            {tests.error && !tests.waiting ? (
              <ErrorState variant="list" title={t("tests.loadTestsFailed")} onRetry={tests.retry} />
            ) : tests.loading ? (
              <LoadingRows rows={3} />
            ) : tests.data?.length ? (
              <TestList tests={tests.data} variant="compact" />
            ) : (
              <EmptyState variant="list" title={t("tests.noTestsTitle")} message={t("tests.noTestsMessage")} />
            )}
          </DashboardPanel>

          <DashboardPanel
            title={t("tasks.openTasks")}
            subtitle={tasks.data ? t("home.openTasksCount", { count: tasks.data.length }) : t("tasks.loading")}
            toolbar={<ViewModeToggle value={taskView} onChange={setTaskView} />}
          >
            <TaskList
              items={tasks.data || []}
              showSubjectTag
              loading={tasks.loading}
              hasError={Boolean(tasks.error) && !tasks.waiting}
              onRetry={tasks.retry}
              viewMode={taskView}
            />
          </DashboardPanel>

          <DashboardPanel
            title={t("home.upcomingEvents")}
            subtitle={events.data ? t("home.upcomingCount", { count: upcoming.length }) : t("events.loading")}
          >
            {events.error && !events.waiting ? (
              <ErrorState variant="list" title={t("events.loadFailed")} onRetry={events.retry} />
            ) : events.loading ? (
              <LoadingRows rows={3} />
            ) : (
              upcoming.length === 0 && (
                <EmptyState variant="list" title={t("home.noUpcomingEvents")} message={t("home.noUpcomingEventsMessage")} />
              )
            )}
            {!(events.error && !events.waiting) && upcoming.map((event) => (
              <button
                type="button"
                key={event.id}
                className="mmm-event-row"
                onClick={() => navigate(event.subjectId ? `/subjects/${event.subjectId}/calendar` : "/calendar")}
              >
                <ColorDot color={EVENT_TYPE_COLORS[event.type].text} />
                <span className="mmm-event-row-title">{event.title}</span>
                {event.subjectTitle && <span className="mmm-event-row-subject">{event.subjectTitle}</span>}
                <DueBadge startDate={event.startDate} />
              </button>
            ))}
          </DashboardPanel>
        </Grid>
      </div>
    </div>
  );
}
